import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ImageZoom from '../components/ImageZoom';
// import TabSwitcher from '../components/TabSwitcher'; // REMOVED TabSwitcher import
import client from '../utils/sanityClient'; // Import Sanity client
import { useLanguage, t } from '../utils/i18n'; // Import i18n
import ProductCard from '../components/ProductCard'; // Import ProductCard for related products
import { useCart } from '../contexts/CartContext'; // Import cart context

const ProductDetailPage = () => {
  const { slug } = useParams(); // Use slug instead of id
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const { language } = useLanguage();
  const { addToCart } = useCart();
  // const [activeTab, setActiveTab] = useState('description'); // REMOVED activeTab state

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) {
        console.log('No slug provided');
        setLoading(false);
        setProduct(null);
        return;
      }
      try {
        setLoading(true);
        console.log('Fetching product with slug:', slug);
        
        // Test Sanity connection first
        try {
          const testQuery = await client.fetch('*[_type == "product"][0...1]');
          console.log('Sanity connection test:', testQuery);
        } catch (error) {
          console.error('Failed to connect to Sanity:', error);
          throw new Error('Failed to connect to Sanity');
        }
        
        // Fetch the product with the matching SLUG from Sanity
        const query = `*[_type == "product" && slug.current == $slug][0] { 
          _id,
          name,
          "slug": slug.current,
          price,
          description,
          stock,
          alto,
          ancho,
          profundidad,
          details,
          "category": category->{name, "slug": slug.current},
          images[]{ 
            _key,
            alt,
            principal,
            asset->{
              _id,
              url 
            }
          }
        }`;
        
        console.log('Executing Sanity query:', query);
        console.log('With parameters:', { slug });
        
        const sanityProduct = await client.fetch(query, { slug });
        
        console.log('Sanity response:', sanityProduct);
        
        if (sanityProduct) {
          // Map Sanity product
          const mainImage = sanityProduct.images?.find(img => img.principal) || sanityProduct.images?.[0];
          const mappedProduct = {
            id: sanityProduct._id,
            name: sanityProduct.name,
            slug: sanityProduct.slug,
            image: mainImage?.asset?.url || '/assets/icons/placeholder.png',
            images: sanityProduct.images,
            price: sanityProduct.price || 0,
            description: sanityProduct.description || '',
            alto: sanityProduct.alto,
            ancho: sanityProduct.ancho,
            profundidad: sanityProduct.profundidad,
            details: sanityProduct.details || '',
            stock: sanityProduct.stock || 0,
            category: sanityProduct.category?.name,
            categorySlug: sanityProduct.category?.slug,
            _original: sanityProduct 
          };
          console.log('Mapped product:', mappedProduct);
          setProduct(mappedProduct);
          
          // Fetch related products (same category slug, excluding current product)
          if (mappedProduct.categorySlug) {
            const related = await client.fetch(`
              *[_type == "product" && slug.current != $currentSlug && category->slug.current == $categorySlug][0...4] {
                _id,
                name,
                "slug": slug.current,
                price,
                images[]{ principal, asset->{url} }
              }
            `, { currentSlug: slug, categorySlug: mappedProduct.categorySlug });

            const mappedRelated = related.map(p => {
              const img = p.images?.find(i => i.principal) || p.images?.[0];
              return {
                id: p._id,
                name: p.name,
                slug: p.slug,
                image: img?.asset?.url || '/assets/icons/placeholder.png',
                price: p.price || 0
              }
            });
            setRelatedProducts(mappedRelated);
          }
        } else {
          console.log('No product found with slug:', slug);
          setProduct(null);
        }
      } catch (error) {
        console.error('Failed to load product from Sanity:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [slug]); // Re-fetch when slug changes

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  // --- Loading State --- 
  if (loading) {
    return (
      <div className="relative min-h-screen"> {/* Main container */}
        {/* New Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] z-0" />
        {/* Old background removed */}
        <div className="relative z-10 py-16">
          <div className="container-custom">
            <p className="text-center text-xl text-akasha-brown">{t('product.loading', language.code)}</p> {/* Adjusted text color */}
          </div>
        </div>
      </div>
    );
  }

  // --- Product Not Found State --- 
  if (!product) {
    return (
      <div className="relative min-h-screen"> {/* Main container */}
        {/* New Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] z-0" />
        {/* Old background removed */}
        <div className="relative z-10 py-16">
          <div className="container-custom">
            {/* Replaced glassStyles.static with Tailwind, adjusted text colors */}
            <div className="p-8 rounded-[2rem] text-center bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
              <h2 className="text-2xl font-bold text-akasha-brown mb-4">{t('product.notFound.title', language.code)}</h2>
              <p className="text-akasha-black/80 mb-6">{t('product.notFound.message', language.code)}</p>
              <Link 
                to="/catalog" 
                // Replaced glassStyles.button with Tailwind button classes
                className="inline-block text-akasha-brown px-6 py-3 rounded-full border border-akasha-brown hover:bg-akasha-brown hover:text-white transition-colors duration-300 focus:outline-none"
              >
                {t('product.notFound.backButton', language.code)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Main Product Display --- 
  return (
    <div className="relative min-h-screen"> {/* Main container */}
      {/* New Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] z-0" />
      {/* Old background removed */}

      <div className="relative z-10 py-16">
        <div className="container-custom">
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  {/* Breadcrumb text colors adjusted */}
                  <Link to="/" className="text-gray-700 hover:text-akasha-brown">
                    {t('breadcrumb.home', language.code)}
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-500 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/></svg>
                    <Link to="/catalog" className="ml-1 text-sm font-medium text-gray-700 hover:text-akasha-brown md:ml-2">
                      {t('breadcrumb.catalog', language.code)}
                    </Link>
                  </div>
                </li>
                {product.category && (
                  <li>
                    <div className="flex items-center">
                      <svg className="w-3 h-3 text-gray-500 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/></svg>
                      <Link to={`/catalog?category=${encodeURIComponent(product.category)}`} className="ml-1 text-sm font-medium text-gray-700 hover:text-akasha-brown md:ml-2">
                        {product.category}
                      </Link>
                    </div>
                  </li>
                )}
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-500 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/></svg>
                    <span className="ml-1 text-sm font-medium text-akasha-brown md:ml-2">{product.name}</span>
                  </div>
                </li>
              </ol>
            </nav>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
            {/* Image Gallery with Zoom - card/container might need glass */}
            <motion.div 
              className="rounded-[2rem] overflow-hidden bg-white/10 backdrop-blur-md border border-white/20 shadow-lg p-4 md:p-6" // Added glass style to container
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ImageZoom 
                src={product.image} 
                alt={product.name} 
                images={product.images} // Pass all images to ImageZoom
              />
            </motion.div>

            {/* Product Info - card/container might need glass */}
            <motion.div 
              className="p-6 md:p-8 rounded-[2rem] bg-white/10 backdrop-blur-md border border-white/20 shadow-lg"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-akasha-brown mb-3 font-serif">{product.name}</h1>
              <p className="text-2xl text-akasha-brown mb-6 font-serif">${product.price.toLocaleString()}</p>
              
              {/* Display main product description directly if it was part of tabs before */}
              {product.description && (
                <div className="text-akasha-black/80 mb-6 prose prose-sm max-w-none">
                  {typeof product.description === 'string' ? product.description : t('product.tabs.descriptionPlaceholder', language.code)}
                </div>
              )}
              
              {/* Measurements / Dimensions */}
              {(product.alto || product.ancho || product.profundidad) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-akasha-brown mb-2">{t('product.dimensions', language.code)}</h3>
                  <ul className="text-akasha-black/80">
                    {product.alto && <li>{t('product.height', language.code) || 'Alto'}: {product.alto} cm</li>}
                    {product.ancho && <li>{t('product.width', language.code) || 'Ancho'}: {product.ancho} cm</li>}
                    {product.profundidad && <li>{t('product.depth', language.code) || 'Profundidad'}: {product.profundidad} cm</li>}
                  </ul>
                </div>
              )}
              
              {product.stock !== null && product.stock > 0 && (
                <div className="mb-6">
                  <label htmlFor="quantity" className="block text-sm font-medium text-akasha-black mb-1">{t('product.quantity', language.code)}:</label>
                  <input 
                    type="number" 
                    id="quantity" 
                    name="quantity" 
                    min="1" 
                    max={product.stock}
                    value={quantity} 
                    onChange={(e) => {
                      const newQuantity = parseInt(e.target.value, 10) || 1;
                      const limitedQuantity = Math.min(Math.max(1, newQuantity), product.stock);
                      setQuantity(limitedQuantity);
                    }}
                    className="w-20 p-2 border border-gray-300 rounded-md focus:ring-akasha-brown focus:border-akasha-brown text-akasha-black"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    {t('product.maxAvailable', language.code, { max: product.stock })}
                  </p>
                </div>
              )}
              
              {product.stock !== null && product.stock > 0 ? (
                <motion.button 
                  onClick={handleAddToCart} 
                  className="w-full bg-akasha-brown text-white py-3 px-6 rounded-lg hover:bg-akasha-brown/90 transition-colors duration-300 font-semibold focus:outline-none focus:ring-2 focus:ring-akasha-brown focus:ring-offset-2"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('product.addToCart.button', language.code)}
                </motion.button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                      {t('product.onRequest.title', language.code)}
                    </h3>
                    <p className="text-sm text-yellow-700">
                      {t('product.onRequest.description', language.code)}
                    </p>
                  </div>
                  <motion.a
                    href={`https://wa.me/5511605388?text=${encodeURIComponent(
                      `Hola, me interesa el producto "${product.name}" que vi en su catálogo. ¿Podrían proporcionarme más información sobre disponibilidad y entrega?`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors duration-300 font-semibold focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    {t('product.onRequest.button', language.code)}
                  </motion.a>
                </div>
              )}
              {/* Stock status display */}
              {product.stock !== null && product.stock !== undefined && product.stock > 0 && (
                <p className="text-sm text-green-600 mt-2">{t('product.inStock', language.code, { count: product.stock })}</p>
              )}
            </motion.div>
          </div>

          {/* Related Products Section */}
          {relatedProducts.length > 0 && (
            <div className="mt-16 md:mt-24">
              <h2 className="text-3xl font-cursive text-akasha-brown text-center mb-10">
                {t('product.related', language.code)}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {relatedProducts.map((relatedProduct, index) => (
                  <ProductCard 
                    key={relatedProduct.id} 
                    product={relatedProduct} 
                    // No glassStyles prop needed for ProductCard as it handles its own style
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
